import { useEffect, useState } from 'react';

/**
 * Decides whether a logo tile should be LIGHT or DARK based on the logo's own
 * average brightness. White/light logos need a dark tile for contrast; dark
 * logos need a light one.
 *
 * On load, draws the image to an offscreen canvas, samples average luminance,
 * and returns 'dark' (logo is light → use a dark tile) or 'light' (logo is dark
 * → use a light tile). Best-effort: if the image is cross-origin-tainted (CORS
 * blocks pixel reads) or sampling fails, returns 'light' as a safe default.
 *
 * @param src the logo URL (may be null/undefined → no detection)
 * @returns 'light' | 'dark' — which TILE background to use for best contrast.
 */
export type LogoTileTone = 'light' | 'dark';

// Cache per URL so the same logo (e.g. a company shown in many rows) is sampled
// once, not once-per-mount. Shared across all callers.
const toneCache = new Map<string, LogoTileTone>();

function sampleTone(src: string): Promise<LogoTileTone> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Downscale to keep the canvas pass cheap — 32×32 is enough for an
        // average-brightness reading; logos are simple graphics, not photos.
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve('light');
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        // Average luminance (Rec. 601) of non-fully-transparent pixels. A
        // transparent logo on a clear bg skews toward the bg's pixels — those
        // read as transparent here, so we skip them and measure the ink itself.
        let sum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 16) continue; // skip (near-)transparent pixels
          // Premultiply by alpha so faint anti-aliased edges don't skew bright.
          const r = data[i] * (a / 255);
          const g = data[i + 1] * (a / 255);
          const b = data[i + 2] * (a / 255);
          sum += 0.299 * r + 0.587 * g + 0.114 * b;
          count++;
        }
        if (count === 0) return resolve('light'); // fully transparent → no ink
        const avg = sum / count;
        // Threshold: >140 (0-255) counts as a LIGHT logo → needs a DARK tile.
        resolve(avg > 140 ? 'dark' : 'light');
      } catch {
        // Tainted canvas (CORS) — can't read pixels. Safe default.
        resolve('light');
      }
    };
    img.onerror = () => resolve('light');
    img.src = src;
  });
}

export function useLogoTileTone(src?: string | null): LogoTileTone {
  const [tone, setTone] = useState<LogoTileTone>(() =>
    src ? (toneCache.get(src) ?? 'light') : 'light',
  );

  useEffect(() => {
    if (!src) { setTone('light'); return; }
    const cached = toneCache.get(src);
    if (cached) { setTone(cached); return; }
    let cancelled = false;
    // Default to light until measured; re-sampling on the next tick avoids a
    // flash only if the cache was empty.
    setTone('light');
    sampleTone(src).then((result) => {
      if (cancelled) return;
      toneCache.set(src, result);
      setTone(result);
    });
    return () => { cancelled = true; };
  }, [src]);

  return tone;
}
